import { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import { Autocomplete, FormControlLabel, Grid, Radio, RadioGroup, Stack } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import AuthContext from 'units/auth/AuthContext'
import Scanner from 'units/scanner/Scanner.component'
import PlatformContext from 'context/platform/PlatformContext'
// import { palette } from 'assets/theme'
import colors from 'assets/theme/base/colors'
import Page from '../pages/Page'
// import FormField from 'components/forms/FormField'
import AppFormContainer from 'components/forms/AppFormContainer'
import AppFormSection from 'components/forms/AppFormSection'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDBadge from 'components/MDBadge'
// import AppForm from 'components/forms/AppForm'
// import { addImagesToFormData } from 'helpers/file.helper'
import Tips from 'components/Tips'
import { fileToDataUrl, prepareFormData } from 'helpers/file.helper'
import schema from 'schemas/check'
import documentTitle from 'data-organizers/documents/documentTitle'
import TextField from '@mui/material/TextField'
import { useQuery } from '@tanstack/react-query'
import useQueryLibrary from '../../hooks/useQueryLibrary'
import FetchContext from '../../context/fetch/FetchContext'
import { imageErrorLabel } from './imageError'
import { imageCaptions } from './imageCaptions'
import useValidations from '../../hooks/useValidations'

yup.setLocale({
    mixed: {
        default: 'field.validation.invalid',
    },
    number: {
        min: ({ min }) => ({ key: 'field.validation.number.too_small', values: { min } }),
        max: ({ max }) => ({ key: 'field.validation.number.too_big', values: { max } }),
        lessThan: ({ value }) => ({ key: 'field.validation.number', values: { value } }),
        moreThan: ({ value }) => ({ key: 'field.validation.number', values: { value } }),
        positive: ({ value }) => ({ key: 'field.validation.number.positive', values: { value } }),
        negative: ({ value }) => ({ key: 'field.validation.number.negative', values: { value } }),
        integer: ({ value }) => ({ key: 'field.validation.number.i', values: { value } }),
        truncate: ({ value }) => ({ key: 'field.validation.number.truncate', values: { value } }),
        round: ({ value }) => ({ key: 'field.validation.number.round', values: { value } }),
    },
})

const imagesSpec = [
    {
        key: 'F2',
        fieldName: 'Check_Front_jpg',
        imageType: 'jpg',
        label: imageCaptions.Check_Front_jpg,
        canDisplayImage: true,
    },
    {
        key: 'R2',
        fieldName: 'Check_Back_jpg',
        imageType: 'jpg',
        label: imageCaptions.Check_Back_jpg,
        canDisplayImage: true,
    },
    {
        key: 'F1',
        fieldName: 'Check_Front_tif',
        imageType: 'tif',
        label: imageCaptions.Check_Front_tif,
        canDisplayImage: false,
    },
    {
        key: 'R1',
        fieldName: 'Check_Back_tif',
        imageType: 'tif',
        label: imageCaptions.Check_Back_tif,
        canDisplayImage: false,
    },
]

function CheckEditor({
    document,
    mode,
    documentCommands,
    busy,
    readOnly,
    setDocumentDetailMutation,
    hasVouchers,
}) {
    const { token } = useContext(AuthContext)

    const { clearValidations } = useValidations()

    const scheduledChecksEnabled = false

    const [initialValues, setInitialValues] = useState({
        number: '',
        bank: '',
        branch: '',
        account: '',
        currency: '',
        schedule: 'immediate',
        scheduled_date: null,
        payee_id: null,
    })

    const defaultImages = imagesSpec.reduce((result, imageSpec) => ({
        ...result,
        [imageSpec.fieldName]: null,
    }), {})
    const [images, setImages] = useState(defaultImages)

    const [validated, setValidated] = useState(false)
    // const [disableDeferredOption, setDisableDeferredOption] = useState(false)

    const platform = useContext(PlatformContext)

    useEffect(() => {
        setInitialStateFunc()
    }, [mode])

    const createImagesObject = (imagesDetails) => {
        const imagesObject = imagesSpec.reduce((result, imageSpec) => {
            const imageDetailFound = imagesDetails.find(imageDetail => imageDetail.type === imageSpec.fieldName)
            result[imageSpec.fieldName] = {
                name: imageSpec.fieldName,
                url: imageDetailFound ? `${process.env.REACT_APP_STRAPIURL_BASE}${imageDetailFound.file?.url}?jwt=${token}` : null,
                imageType: imageSpec.imageType,
                provenance: imageDetailFound?.provenance ?? null,
            }
            return result
        }, {})

        return imagesObject
    }

    const setInitialStateFunc = () => {
        if (mode !== 'new' && document.check) {
            const { check, schedule, scheduled_date } = document

            const objectToSet = {
                number: check.number,
                bank: check.bank,
                branch: check.branch,
                account: check.account,
                currency: check.currency,
                schedule,
                scheduled_date: scheduled_date == null ? null : moment(scheduled_date),
                payee_id: check.payee?.id,
            }
            setInitialValues(objectToSet)

            const documentImagesObject = createImagesObject(document.images)
            const editedImagesObject =
                Object.entries(images)
                    .filter(([, value]) => value instanceof File)
                    .reduce((result, [key, value]) => {
                        return {
                            ...result,
                            [key]: value,
                        }
                    }, {})
            setImages({
                ...documentImagesObject,
                ...editedImagesObject,
            })
        }
    }

    const queryLibrary = useQueryLibrary()

    const fetcher = useContext(FetchContext)

    const { data: postBankPayees = [] } = useQuery({
        queryKey: queryLibrary.postBankPayeesQuery(),
        queryFn: async () => {
            if (!scheduledChecksEnabled) {
                return null
            }

            const data = await fetcher.fetchPostBankPayees()
            return data
        },
    })

    console.log('postBankPayees', postBankPayees)

    // const reset = () => {
    //   setFormData({
    //     ...initialValues,
    //   })
    //   setPayload({
    //     images: {},
    //   })
    // }

    // const validate = (event) => {
    //   const form = event.currentTarget;
    //   if (form.checkValidity() === false) {
    //     event.preventDefault();
    //     event.stopPropagation();
    //   }

    //   setValidated(true)
    // }

    const handleSubmit = async (values) => {
        const data = prepareFormData(values, imagesSpec, platform)
        setDocumentDetailMutation.mutate({
            data,
            endpoint: 'Check',
        })
    }

    const handleFileChange = async (event, imageSpec) => {
        const file = event.target.files[0]
        const { fieldName } = imageSpec
        if (file) {
            const dataUrl = await fileToDataUrl(file)

            setImages({
                ...images,
                [fieldName]: {
                    imageType: imageSpec.imageType,
                    name: fieldName,
                    url: dataUrl,
                },
            })
        }
    }

    // const insertScannedData = (rawScannerText) => {
    //   try {
    //     const scannerParser = new ScannerParser(platform)
    //     const result = scannerParser.parseScannerText(rawScannerText, baseLocation)
    //     const {
    //       fieldsData,
    //       imagesObject,
    //       missingImages,
    //     } = result

    //     // setFormData({
    //     //   ...formData,
    //     //   number: fieldsData.number,
    //     //   bank: fieldsData.bank,
    //     //   branch: fieldsData.branch,
    //     //   account: fieldsData.account,
    //     //   schedule: fieldsData.schedule,
    //     //   images: imagesObject,
    //     // })
    //     // setPayload({
    //     //   ...payload,
    //     //   number: fieldsData.number,
    //     //   bank: fieldsData.bank,
    //     //   branch: fieldsData.branch,
    //     //   account: fieldsData.account,
    //     //   schedule: fieldsData.schedule,
    //     //   images: imagesObject,
    //     // })

    //     if (missingImages) {
    //       platform.showMessageBoxSync({
    //         type: 'warning',
    //         buttons: [],
    //         title: 'קבצים לא התקבלו',
    //         message: 'אחד (או יותר) מהקבצים שהסורק אמור לייצר לא נמצאו, יש לחפש ולהעלות אותם ידנית.',
    //       })
    //     }
    //   }
    //   catch(error) {
    //     if(error instanceof ScannerParserError) {
    //       platform.showMessageBoxSync({
    //         type: 'error',
    //         buttons: [],
    //         title: error.title,
    //         message: error.message,
    //       })
    //     }
    //     else {
    //       platform.showMessageBoxSync({
    //         type: 'error',
    //         buttons: [],
    //         title: 'ארעה שגיאה',
    //         message: error.message,
    //       })
    //     }
    //   }
    // }

    // const doActivateScanner = async () => {
    //   const scannedData = await activateScanner(platform)
    //   console.log('scannedData', scannedData)
    //   if (scannedData) {
    //     insertScannedData(scannedData)
    //   }
    // }

    // const onActivateScanner = (e) => {
    //   e.preventDefault()
    //   e.stopPropagation()
    //   // doActivateScanner()
    //   // // const scannedData = await activateScanner()
    //   // // console.log('scannedData', scannedData)
    //   // // if (scannedData) {
    //   // //   insertScannedData(scannedData)
    //   // // }
    // }

    const [scannerTrigger, setScannerTrigger] = useState(false)

    const mimicScanner = async (mode) => {
        const modes = ['valid', 'invalid', 'empty']
        if (!modes.includes(mode)) {
            console.log('not a valid mode', mode)
        }

        const route = `/tools/mimic${mode}checkscan`
        setScannerTrigger(route)

        // try {
        //   const res = await fetch({
        //     method: 'get',
        //     path: route,
        //     headers: {
        //       'Content-Type': 'application/json',
        //     },
        //     responseHandler: (response) => response,
        //     rethrow: true,
        //   })

        //   // File uploaded successfully!
        //   // insertScannedData(res.data)
        // }
        // catch (error) {
        //   // File upload failed!
        //   console.error(`couldn't fetch mimic of scan`, error)
        // }
    }

    // const handleClick = (e, handler) => {
    //   e.preventDefault()
    //   handler()
    // }

    const isSaveDisabled = busy || readOnly || scannerTrigger

    const commands = [
        ...documentCommands,
        {
            label: 'הפעלת סורק',
            onClick: (e) => {
                setScannerTrigger(true)
                // onActivateScanner(e)
            },
            position: 'start',
            color: 'secondary',
            disabled: isSaveDisabled,
        },
        // {
        //   label: 'מק',
        //   // to: null,
        //   onClick: () => {
        //     mimicScanner('fakemac')
        //   },
        //   position: 'end',
        //   color: 'secondary',
        //   disabled: busy,
        // },
        // {
        //   label: 'חיקוי סורק תקין',
        //   // to: null,
        //   onClick: () => mimicScanner('valid'),
        //   position: 'end',
        //   color: 'secondary',
        //   disabled: busy,
        // },
        // {
        //   label: 'חיקוי סורק לא תקין',
        //   // to: null,
        //   onClick: () => mimicScanner('invalid'),
        //   position: 'end',
        //   color: 'secondary',
        //   disabled: busy,
        // },
        // {
        //   label: 'חיקוי סורק ריק',
        //   // to: null,
        //   onClick: () => mimicScanner('empty'),
        //   position: 'end',
        //   color: 'secondary',
        //   disabled: busy,
        // },
        // {
        //   label: 'הפעלת סורק',
        //   onClick: (e) => handleClick(e, () => onActivateScanner()),
        //   position: 'start',
        // },
        // {
        //   label: 'חיקוי סורק תקין',
        //   onClick: (e) => handleClick(e, () => mimicScanner('valid')),
        //   position: 'end',
        //   color: 'secondary',
        // },
        // {
        //   label: 'חיקוי סורק לא תקין',
        //   onClick: (e) => handleClick(e, () => mimicScanner('invalid')),
        //   position: 'end',
        //   color: 'secondary',
        // },
        // {
        //   label: 'חיקוי סורק ריק',
        //   onClick: (e) => handleClick(e, () => mimicScanner('empty')),
        //   position: 'end',
        //   color: 'secondary',
        // },
    ]

    // const MyField = ({
    //   label,
    //   isInvalid,
    //   validationMessage,
    //   // validated,
    //   ...props
    // }) => {
    //   const [field, meta, helpers] = useField(props)
    //   return (
    //     <TextField
    //       variant="filled"
    //       label={label}
    //       {...field}
    //       {...props}
    //     />
    //     // <div>
    //     //   <label>
    //     //     {label}
    //     //   </label>
    //     //   <input {...field} {...props} />
    //     //   {meta.touched && meta.error ? (
    //     //     <div className="error">{meta.error}</div>
    //     //   ) : null}
    //     // </div>
    //   )
    // }

    const schedulingTips = []

    const now = moment()
    const dayOfMonth = now.date()

    const intl = useIntl()

    const deferredOptionLabel = intl.formatMessage({ id: 'documents.schedules.deferred' })
    const disableDeferredOption = readOnly || dayOfMonth >= 15
    if (disableDeferredOption) {
        schedulingTips.push(`ניתן לבחור באפשרות ״${deferredOptionLabel}״ רק בתחילת החודש.`)
    }

    const scheduledOptionLabel = intl.formatMessage({ id: 'documents.schedules.scheduled' })
    const disableScheduledOption = scheduledChecksEnabled && (readOnly || hasVouchers)
    if (scheduledChecksEnabled && hasVouchers) {
        schedulingTips.push(`ניתן לבחור באפשרות ״${scheduledOptionLabel}״ רק ברשומות ללא שוברים.`)
    }

    // const paymentSchedulingOptions = [
    //   {
    //     id: 1,
    //     label: 'תשלום שוטף',
    //     disabled: false,
    //   },
    //   {
    //     id: 2,
    //     label: 'תשלום דחוי ל-15 לחודש',
    //     disabled: disableDeferredOption,
    //   },
    // ]

    const schemaDescription = schema.describe()
    const { fields } = schemaDescription

    const page = (
        <Page
            title={documentTitle(document)}
            status={document.status}
            commands={commands}
        >
            <Formik
                onSubmit={async (values, action) => {
                    await handleSubmit(values)
                }}
                enableReinitialize={true}
                validationSchema={schema}
                initialValues={initialValues}
            >
                {({
                    handleChange,
                    values,
                    setFieldValue,
                }) => {
                    function updateItems(items) {
                        if (items) {
                            const fields = {
                                number: items[0],
                                bank: items[1],
                                branch: items[2].slice(0, 3),
                                account: items[3],
                            }
                            // if (fields) {
                            Object.entries(fields).forEach(([key, value]) => {
                                setFieldValue(key, value)
                            })
                            // }
                        }
                    }

                    // function updateImages(imagesObject) {
                    //   Object.entries(imagesObject).forEach(([key, value]) => {
                    //     setFieldValue(`images.${key}`, value)
                    //   })
                    //   setImages(imagesObject)
                    // }

                    return (
                        <Form id="form">
                            <Scanner
                                scannerTrigger={scannerTrigger}
                                setScannerTrigger={setScannerTrigger}
                                setFieldValue={setFieldValue}
                                setImages={setImages}
                                imagesSpec={imagesSpec}
                                updateItems={updateItems}
                                onScanned={clearValidations}
                                // updateImages={updateImages}
                            />
                            <AppFormContainer>
                                <AppFormSection title="תזמון" xs={12} md={12} lg={12}>
                                    <RadioGroup
                                        name="schedule"
                                        onChange={handleChange}
                                        sx={{
                                            flexDirection: {
                                                xs: 'column',
                                                lg: 'row',
                                            },
                                        }}
                                    >
                                        <FormControlLabel
                                            value="immediate"
                                            control={<Radio />}
                                            label={intl.formatMessage({ id: 'documents.schedules.immediate' })}
                                            name="schedule"
                                            checked={values.schedule === 'immediate'}
                                            disabled={readOnly}
                                            sx={{
                                                '& .MuiFormControlLabel-label': {
                                                    fontWeight: readOnly ? 'normal' : 'bold',
                                                    textDecoration: readOnly ? 'line-through' : 'none',
                                                },
                                            }}
                                        />
                                        <FormControlLabel
                                            value="deferred"
                                            control={<Radio />}
                                            label={deferredOptionLabel}
                                            name="schedule"
                                            checked={values.schedule === 'deferred'}
                                            disabled={disableDeferredOption}
                                            sx={{
                                                '& .MuiFormControlLabel-label': {
                                                    fontWeight: disableDeferredOption ? 'normal' : 'bold',
                                                    textDecoration: disableDeferredOption ? 'line-through' : 'none',
                                                },
                                            }}
                                        />
                                        {scheduledChecksEnabled && (
                                            <FormControlLabel
                                                value="scheduled"
                                                control={<Radio />}
                                                label={scheduledOptionLabel}
                                                name="schedule"
                                                checked={values.schedule === 'scheduled'}
                                                disabled={disableScheduledOption}
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        fontWeight: disableScheduledOption ? 'normal' : 'bold',
                                                        textDecoration: disableScheduledOption ? 'line-through' : 'none',
                                                    },
                                                }}
                                            />
                                        )}
                                    </RadioGroup>
                                    <Tips tips={schedulingTips} />
                                </AppFormSection>
                                <AppFormSection
                                    title="תשלום מתוזמן"
                                    xs={12}
                                    md={12}
                                    lg={12}
                                    disabled={disableScheduledOption}
                                    sx={{
                                        display: values.schedule === 'scheduled' ? 'block' : 'none',
                                    }}
                                >
                                    <Stack
                                        gap={4}
                                        sx={{
                                            flexDirection: 'row',
                                        }}
                                    >
                                        <Autocomplete
                                            xs={12}
                                            lg={8}
                                            sx={{
                                                width: '60%',
                                                height: 40,

                                                '& .MuiOutlinedInput-root': {
                                                    padding: '5px',
                                                },
                                            }}
                                            value={
                                                postBankPayees?.find((payee) => payee.id === values.payee_id)
                                                ?? null
                                            }
                                            options={postBankPayees}
                                            getOptionLabel={(option) => option?.account_name || ''}
                                            getOptionKey={(option) => option.id}
                                            renderInput={(params) => (
                                                <TextField {...params} label={'חשבון להפקדה'} />
                                            )}
                                            onChange={(e, newValue) => {
                                                return setFieldValue('payee_id', newValue?.id)
                                            }}
                                            label={'חשבון להפקדה'}
                                            noOptionsText={'לא נמצאו חשבונות'}
                                            disabled={disableScheduledOption}
                                        />
                                        <DatePicker
                                            xs={12}
                                            lg={4}
                                            name="scheduled_date"
                                            value={values.scheduled_date}
                                            onChange={(newValue) => setFieldValue('scheduled_date', newValue)}
                                            format="DD/MM/YYYY"
                                            disabled={disableScheduledOption}
                                            sx={{
                                                width: 200,
                                                height: 50,
                                            }}
                                        />
                                    </Stack>
                                </AppFormSection>
                                <AppFormSection
                                    title="פרטי השיק"
                                    section="details"
                                    fields={fields}
                                    disabled={readOnly}
                                    xs={12}
                                    md={6}
                                    lg={4}
                                />
                                <AppFormSection title="תמונות" xs={12} md={12} lg={8}>
                                    <Grid container item columns={8}>
                                        {imagesSpec.map((imageSpec, index) => {
                                            const { label, fieldName, canDisplayImage } = imageSpec

                                            const image = images[fieldName]

                                            const url = image?.url

                                            let badgeMessage = null
                                            let badgeColor = null
                                            if (url == null) {
                                                badgeMessage = 'לא נבחרה תמונה'
                                                badgeColor = 'warning'
                                            }
                                            else if (image?.imageType === 'tif') {
                                                badgeMessage = 'קיימת תמונה, אבל אי אפשר להציג אותה'
                                                badgeColor = 'success'
                                            }

                                            // Per-image duplicate/missing state, independent of the
                                            // display badge above; cleared when the image is replaced,
                                            // since a freshly selected file carries no provenance.
                                            const errorMessage = imageErrorLabel(image?.provenance)

                                            let src
                                            if (url != null) {
                                                if (url.includes('://')) {
                                                    src = url
                                                }
                                                else if (url.startsWith('/')) {
                                                    src = `file://${url}`
                                                }
                                                else {
                                                    src = url
                                                }
                                            }

                                            return (
                                                <Grid item key={index} xs={4}>
                                                    <label className="btn btn-primary" htmlFor={fieldName}>
                                                        <MDBox
                                                            mx={1}
                                                            mb={2}
                                                            py={1}
                                                            px={2}
                                                            bgColor={colors.grey['200']}
                                                            shadow="lg"
                                                            sx={{
                                                                borderRadius: 2,
                                                                border: errorMessage ? 2 : 0,
                                                                borderColor: 'error.main',
                                                                cursor: readOnly ? 'not-allowed' : 'pointer',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                            }}
                                                        >
                                                            <MDTypography variant="h3" pb={1}>
                                                                {label}
                                                            </MDTypography>
                                                            {badgeMessage && (
                                                                <MDBadge
                                                                    badgeContent={badgeMessage}
                                                                    color={badgeColor}
                                                                    circular={true}
                                                                    variant="contained"
                                                                    size="lg"
                                                                />
                                                            )}
                                                            {errorMessage && (
                                                                <MDBadge
                                                                    badgeContent={errorMessage}
                                                                    color="error"
                                                                    circular={true}
                                                                    variant="contained"
                                                                    size="lg"
                                                                />
                                                            )}
                                                            {canDisplayImage && image?.url && (
                                                                <img alt={label} src={src} />
                                                            )}
                                                            <MDBox>
                                                                <MDBox>
                                                                    <input
                                                                        type="file"
                                                                        name={fieldName}
                                                                        id={fieldName}
                                                                        disabled={readOnly}
                                                                        style={{ display: 'none' }}
                                                                        onChange={(e) => {
                                                                            setFieldValue(`images.${fieldName}`, e.currentTarget.files[0])
                                                                            handleFileChange(e, imageSpec)
                                                                        }}
                                                                    />
                                                                </MDBox>
                                                            </MDBox>
                                                            <MDTypography
                                                                color="primary"
                                                                pt={1}
                                                                sx={{
                                                                    textAlign: 'end',
                                                                }}
                                                            >
                                                                בחירת קובץ באופן ידני
                                                            </MDTypography>
                                                        </MDBox>
                                                    </label>
                                                </Grid>
                                            )
                                        })}
                                    </Grid>
                                </AppFormSection>
                            </AppFormContainer>
                        </Form>
                    )
                }}
            </Formik>
        </Page>
    )

    return page
}

export default CheckEditor
